import { inject, injectable } from "inversify";
import { DependencyType } from "@di/DependencyType";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { isLoggedOutMeApolloError } from "@modules/apollo/authError.util";
import { syncSentryUser } from "@modules/authentication/sentryUser";
import {
  MeDocument,
  MeQuery,
  MeQueryVariables,
  RequestMagicLinkDocument,
  RequestMagicLinkMutation,
  RequestMagicLinkMutationVariables,
} from "generated/graphql";
import { UserRole } from "@modules/authentication/roles";

@injectable()
export class AuthenticationService {
  constructor(
    @inject(DependencyType.ApolloClient)
    private readonly apolloClient: ApolloClient<NormalizedCacheObject>
  ) {}

  async requestMagicLink(email: string) {
    const mutationTuple = await this.apolloClient.mutate<
      RequestMagicLinkMutation,
      RequestMagicLinkMutationVariables
    >({
      mutation: RequestMagicLinkDocument,
      variables: {
        input: {
          email,
        },
      },
    });

    if (mutationTuple.errors || !mutationTuple.data?.requestMagicLink) {
      throw new Error("Failed to send magic link");
    }

    return true;
  }

  async me(clearCache = false) {
    const queryTuple = await this.apolloClient
      .query<MeQuery, MeQueryVariables>({
        query: MeDocument,
        fetchPolicy: clearCache ? "no-cache" : "cache-first",
      })
      .catch((error) => {
        if (isLoggedOutMeApolloError(error)) {
          return undefined;
        }
        throw error;
      });

    if (!queryTuple) {
      syncSentryUser(null);
      return undefined;
    }

    if (queryTuple.errors || !queryTuple.data?.me) {
      syncSentryUser(null);
      return undefined;
    }

    try {
      localStorage.setItem(
        "fullname_cache",
        queryTuple.data.me?.fullName ?? queryTuple.data.me?.email ??
          ""
      );
    } catch {
      /* localstorage error */
    }

    const user = {
      ...queryTuple.data.me,
      role: this.getCachedRole(),
    };

    syncSentryUser(user);

    return user;
  }

  async logout() {
    await this.apolloClient.clearStore();
    syncSentryUser(null);
    try {
      localStorage.setItem("fullname_cache", "");
      localStorage.setItem("role_cache", "");
    } catch {
      /* localstorage error */
    }
  }

  getCachedFullname() {
    try {
      return localStorage.getItem("fullname_cache");
    } catch {
      return undefined;
    }
  }

  getCachedRole() {
    try {
      const cachedRole = localStorage.getItem("role_cache");
      if (!cachedRole) {
        return undefined;
      }

      // Check if the role is valid
      if (!Object.values(UserRole).includes(cachedRole as UserRole)) {
        return undefined;
      }

      return cachedRole as UserRole;
    } catch {
      return undefined;
    }
  }

  // TODO: Implement mobile auth verification with new backend
  async verifyMobileAuthRequest(_token: string, _requestId: string): Promise<boolean> {
    throw new Error("Mobile authentication not yet implemented in new backend");
  }
}
